import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, Slide } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { Icons } from "assets";
import { useFormik } from "formik";
import CustomButton from "shared/components/customButton";
import Loader from "shared/components/loader/animation";
import { toastMessage } from "shared/components/toast";
import {
  createCouponService,
  getCouponByIdService,
  updateCouponService,
} from "shared/services/accountantService";
import { coupon_type_enums, coupon_types } from "shared/utils/constants";
import {
  createCouponSchema,
  updateCouponSchema,
} from "shared/utils/validationSchemas";
import styles from "./style.module.scss";

const toDateInputValue = (value: any) =>
  value ? String(value).split("T")[0].split(" ")[0] : "";

interface CreatePromoModalPropsType {
  viewModal: boolean;
  closeViewModal: any;
  confirm: any;
  loader: boolean;
  isEdit: boolean;
  selectedPromo?: any;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CreatePromoModal = ({
  loader,
  viewModal,
  closeViewModal,
  confirm,
  isEdit,
  selectedPromo,
}: CreatePromoModalPropsType) => {
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);

  const formik: any = useFormik({
    initialValues: {
      code: "",
      type: coupon_type_enums.percentage,
      discount_percentage: "",
      flat_spmp: "",
      quantity: "",
      expiry_date: "",
    },
    validationSchema: isEdit ? updateCouponSchema : createCouponSchema,
    onSubmit: (values: any) => {
      handleSubmit(values);
    },
  });

  const isFlatType = Number(formik.values.type) === coupon_type_enums.flat;

  const onClose = () => {
    formik.resetForm();
    closeViewModal(false);
  };

  // Fetch coupon data when editing
  useEffect(() => {
    if (isEdit && selectedPromo?.id && viewModal) {
      getCouponByIdService(selectedPromo.id)
        .then(({ data, status }) => {
          if (status) {
            // Populate form with existing data
            formik.setValues({
              code: data?.data?.code || "",
              type: Number(data?.data?.type) || coupon_type_enums.percentage,
              discount_percentage: data?.data?.discount_percentage ?? "",
              flat_spmp: data?.data?.flat_spmp ?? "",
              quantity: data?.data?.quantity ?? "",
              expiry_date: toDateInputValue(data?.data?.expiry_date),
            });
          }
        })
        .catch((err) => {
          toastMessage("error", err?.response?.data?.message);
          console.log("Error fetching coupon:", err);
        });
    }
  }, [isEdit, selectedPromo?.id, viewModal]);

  // Only the amount field matching the selected type is sent to the API
  const handleTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextType = Number(event.target.value);
    const clearedField =
      nextType === coupon_type_enums.flat ? "discount_percentage" : "flat_spmp";
    formik.setFieldValue("type", nextType);
    formik.setFieldValue(clearedField, "");
    formik.setFieldTouched(clearedField, false);
  };

  const buildPayload = (values: any) => {
    const type = Number(values.type);
    return {
      code: values.code.trim(),
      type,
      quantity: Number(values.quantity),
      expiry_date: values.expiry_date,
      ...(type === coupon_type_enums.flat
        ? { flat_spmp: Number(values.flat_spmp) }
        : { discount_percentage: Number(values.discount_percentage) }),
    };
  };

  const handleSubmit = (values: any) => {
    setSubmitLoader(true);
    const payload = buildPayload(values);
    const request =
      isEdit && selectedPromo?.id
        ? updateCouponService(selectedPromo.id, payload)
        : createCouponService(payload);

    request
      .then(({ status }) => {
        setSubmitLoader(false);
        if (status) {
          onClose();
          confirm();
        }
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message);
        console.log("err", err?.response?.data?.message);
        setSubmitLoader(false);
      });
  };

  return (
    <div>
      <Dialog
        open={viewModal}
        TransitionComponent={Transition}
        keepMounted
        onClose={onClose}
        fullWidth
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogContent className={styles.dialogContent}>
          <div className={styles.crossIcon}>
            <Icons.CrossIcon onClick={() => onClose()} />
          </div>
          <form onSubmit={formik.handleSubmit}>
            <div className={styles.title}>
              {isEdit ? "Edit coupon" : "Create new coupon"}
            </div>

            <div>
              <div className={styles.heading}>
                Coupon Code <span>*</span>
              </div>
              <input
                className={styles.input}
                placeholder="Enter coupon code"
                name="code"
                value={formik.values.code}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.code && formik.errors.code ? (
                <div className={styles.errorText}>{formik?.errors?.code}</div>
              ) : (
                <div style={{ height: "20px" }}></div>
              )}
            </div>

            <div>
              <div className={styles.heading}>
                Coupon Type <span>*</span>
              </div>
              <select
                className={styles.input}
                name="type"
                value={formik.values.type}
                onChange={handleTypeChange}
                onBlur={formik.handleBlur}
              >
                {coupon_types.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {formik.touched.type && formik.errors.type ? (
                <div className={styles.errorText}>{formik?.errors?.type}</div>
              ) : (
                <div style={{ height: "20px" }}></div>
              )}
            </div>

            {isFlatType ? (
              <div>
                <div className={styles.heading}>
                  Flat Student Per Month Price <span>*</span>
                </div>
                <input
                  className={styles.input}
                  placeholder="Enter flat student per month price"
                  name="flat_spmp"
                  type="number"
                  min={1}
                  value={formik.values.flat_spmp}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.flat_spmp && formik.errors.flat_spmp ? (
                  <div className={styles.errorText}>
                    {formik?.errors?.flat_spmp}
                  </div>
                ) : (
                  <div style={{ height: "20px" }}></div>
                )}
              </div>
            ) : (
              <div>
                <div className={styles.heading}>
                  Discount Percentage <span>*</span>
                </div>
                <input
                  className={styles.input}
                  placeholder="Enter discount percentage"
                  name="discount_percentage"
                  type="number"
                  min={1}
                  max={100}
                  value={formik.values.discount_percentage}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.discount_percentage &&
                formik.errors.discount_percentage ? (
                  <div className={styles.errorText}>
                    {formik?.errors?.discount_percentage}
                  </div>
                ) : (
                  <div style={{ height: "20px" }}></div>
                )}
              </div>
            )}

            <div>
              <div className={styles.heading}>
                Quantity <span>*</span>
              </div>
              <input
                className={styles.input}
                placeholder="Enter quantity"
                name="quantity"
                type="number"
                min={1}
                value={formik.values.quantity}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.quantity && formik.errors.quantity ? (
                <div className={styles.errorText}>
                  {formik?.errors?.quantity}
                </div>
              ) : (
                <div style={{ height: "20px" }}></div>
              )}
            </div>

            <div>
              <div className={styles.heading}>
                Expiry Date <span>*</span>
              </div>
              <input
                className={styles.input}
                placeholder="Select expiry date"
                name="expiry_date"
                type="date"
                value={formik.values.expiry_date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.expiry_date && formik.errors.expiry_date ? (
                <div className={styles.errorText}>
                  {formik?.errors?.expiry_date}
                </div>
              ) : (
                <div style={{ height: "20px" }}></div>
              )}
            </div>

            <CustomButton text={isEdit ? "Update" : "Create"} />
          </form>
        </DialogContent>
        {submitLoader && <Loader />}
      </Dialog>
    </div>
  );
};

export default CreatePromoModal;
